import React, { createContext, useContext, useState, useCallback } from 'react';
import { useApiData, ApiData } from './apiService';

interface RequestedMonths {
    [tableName: string]: Set<string>; // Format: "YYYY-MM"
}

interface RequestedTables {
    [tableName: string]: boolean;
}

interface DataManagerContextType {
    requestedMonths: RequestedMonths;
    requestedTables: RequestedTables;
    isGetTableLoading: boolean;
    loadingTables: Set<string>;
    isTableLoading: (tableName: string) => boolean;
    clearTableCache: (table_name: string) => void;
    hasTableData: (table_name: string, month?: string) => boolean;
    requestTableData: (table_name: string, month?: string, parameter?: string) => Promise<void>;
}

const DataManagerContext = createContext<DataManagerContextType | undefined>(undefined);

// Configuration for which tables use month tracking vs simple boolean
const MONTH_TRACKED_TABLES = [
    'schedule_table', 'bottoms_schedule_table', 'vac_schedule_table_vs', 'ngl_schedule_table',
    'crude_main', 'crude_main_pickup', 'crude_main_delivery',
    'changelog',
    'vac_tickets',
    'vac_accounts_payable',
    'ngl_ticket_data',
];

export const DataManagerProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
    const { handleGetTable, handleGetChangeLogTable, apiData, setApiData } = useApiData();
    const [requestedMonths, setRequestedMonths] = useState<RequestedMonths>({});
    const [requestedTables, setRequestedTables] = useState<RequestedTables>({});
    const [isGetTableLoading, setIsGetTableLoading] = useState<boolean>(false);
    const [loadingTables, setLoadingTables] = useState<Set<string>>(new Set());
    const [requestQueue, setRequestQueue] = useState<Array<() => Promise<void>>>([]);
    const [isProcessingQueue, setIsProcessingQueue] = useState<boolean>(false);

    // Process request queue sequentially
    const processQueue = useCallback(async () => {
        if (isProcessingQueue || requestQueue.length === 0) return;
        
        setIsProcessingQueue(true);
        const nextRequest = requestQueue[0];
        
        try {
            await nextRequest();
        } catch (error) {
            console.error('Error processing queued request:', error);
        } finally {
            setRequestQueue(prev => prev.slice(1));
            setIsProcessingQueue(false);
        }
    }, [isProcessingQueue, requestQueue]);

    // Auto-process queue when new requests are added
    React.useEffect(() => {
        if (requestQueue.length > 0 && !isProcessingQueue) {
            processQueue();
        }
    }, [requestQueue, isProcessingQueue, processQueue]);

    // Helper to check if a specific table is loading
    const isTableLoading = useCallback((tableName: string): boolean => {
        return loadingTables.has(tableName);
    }, [loadingTables]);

    // Helper function to clear cache for a specific table
    const clearTableCache = useCallback((table_name: string) => {
        setRequestedTables(prev => ({
            ...prev,
            [table_name]: false
        }));
        
        setRequestedMonths(prev => ({
            ...prev,
            [table_name]: new Set()
        }));
        
        if (apiData) {
            const { [table_name as keyof ApiData]: removed, ...rest } = apiData;
            setApiData(rest as ApiData);
        } else {
            setApiData(null);
        }
    }, [apiData, setApiData]);

    // Helper function to check if data exists for a table
    const hasTableData = useCallback((table_name: string, month?: string): boolean => {
        const isMonthTracked = MONTH_TRACKED_TABLES.includes(table_name);
        
        if (isMonthTracked) {
            const targetMonth = month || new Date().toISOString().slice(0, 7);
            return requestedMonths[table_name]?.has(targetMonth) || false;
        }
        
        return requestedTables[table_name] || false;
    }, [requestedMonths, requestedTables]);

    // Function to request data if not already fetched
    const requestTableData = useCallback(async (table_name: string, month?: string, parameter?: string, replaceData: boolean = false) => {
        const isMonthTracked = MONTH_TRACKED_TABLES.includes(table_name);

        // Create the request function
        const executeRequest = async () => {
            if (isMonthTracked) {
                // Normalize month to YYYY-MM format for caching (internal format)
                let targetMonth: string;
                if (month && /^\d{4}-\d{2}$/.test(month)) {
                    // Already in YYYY-MM format
                    targetMonth = month;
                } else if (month) {
                    // Convert "Month YYYY" format to YYYY-MM for cache key
                    try {
                        const date = new Date(month);
                        targetMonth = date.toISOString().slice(0, 7);
                    } catch {
                        // Fallback to current month if parsing fails
                        targetMonth = new Date().toISOString().slice(0, 7);
                    }
                } else {
                    // No month provided, use current month
                    targetMonth = new Date().toISOString().slice(0, 7);
                }
                
                if (!requestedMonths[table_name]?.has(targetMonth) || replaceData) {
                    // Mark this table as loading
                    setLoadingTables(prev => new Set(Array.from(prev).concat(table_name)));
                    setIsGetTableLoading(true);
                    
                    try {
                        // Convert YYYY-MM format to "Month YYYY" format for backend
                        const [year, monthNum] = targetMonth.split('-');
                        const date = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
                        const backendMonth = date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
                        
                        // For changelog: always merge (accumulate months), never replace
                        // For other tables: respect replaceData parameter
                        if (table_name === 'changelog') {
                            await handleGetChangeLogTable(table_name, backendMonth);
                        } else {
                            await handleGetTable(table_name, backendMonth, replaceData);
                        }
                        
                        setRequestedMonths(prev => ({
                            ...prev,
                            [table_name]: new Set([...Array.from(prev[table_name] || []), targetMonth])
                        }));
                    } catch (error) {
                        console.error(`Error fetching ${table_name} for ${targetMonth}:`, error);
                    } finally {
                        // Remove this table from loading set
                        setLoadingTables(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(table_name);
                            return newSet;
                        });
                        // Only set global loading to false if no tables are loading
                        setLoadingTables(current => {
                            if (current.size === 0) {
                                setIsGetTableLoading(false);
                            }
                            return current;
                        });
                    }
                }
            } else {
                if (!requestedTables[table_name] || replaceData) {
                    // Mark this table as loading
                    setLoadingTables(prev => new Set(Array.from(prev).concat(table_name)));
                    setIsGetTableLoading(true);
                    
                    try {
                        await handleGetTable(table_name, parameter, replaceData);
                        
                        setRequestedTables(prev => ({
                            ...prev,
                            [table_name]: true
                        }));
                    } catch (error) {
                        console.error(`Error fetching ${table_name}:`, error);
                    } finally {
                        // Remove this table from loading set
                        setLoadingTables(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(table_name);
                            return newSet;
                        });
                        // Only set global loading to false if no tables are loading
                        setLoadingTables(current => {
                            if (current.size === 0) {
                                setIsGetTableLoading(false);
                            }
                            return current;
                        });
                    }
                }
            }
        };

        // Add request to queue instead of executing immediately
        setRequestQueue(prev => [...prev, executeRequest]);
    }, [handleGetTable, handleGetChangeLogTable, requestedMonths, requestedTables]);

    const contextValue = React.useMemo(() => ({
        requestedMonths,
        requestedTables,
        isGetTableLoading,
        loadingTables,
        isTableLoading,
        clearTableCache,
        hasTableData,
        requestTableData
    }), [requestedMonths, requestedTables, isGetTableLoading, loadingTables, isTableLoading, clearTableCache, hasTableData, requestTableData]);

    return (
        <DataManagerContext.Provider value={contextValue}>
            {children}
        </DataManagerContext.Provider>
    );
};

export const useDataManager = (): DataManagerContextType => {
    const context = useContext(DataManagerContext);
    if (!context) {
        throw new Error('useDataManager must be used within a DataManagerProvider');
    }
    return context;
};